import { AMSAutocomplete, AMSButton, AMSCurrencyAutocomplete, AMSViewOnlyTextField } from '../../../../helpers/ui';
import {
  AccessPermissions,
  DeliveryRequestResponse,
  DeliveryRequestStatus,
  DeliveryRequestStatusKey,
  DeliveryRequestStatusKeys,
  DeliveryRequestStatusLabels,
  StockReceivingProcessItemResponse,
  UpdateBatchesRequest,
  UpdateDeliveryRequestRequest,
  amsV3Service,
  getDeliveryRequestStatusByKey,
  snackbarService
} from '../../../../services';
import { Grid, Paper } from '@material-ui/core';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  usePermissions,
  useStockReceivingProcess,
  useStockReceivingProcessItems,
  useStyles
} from '../../../../helpers/hooks';

import EditStockReceivingProcessTableComponent from './EditStockReceivingProcessTableComponent';
import Lookup from '../../../../models/lookup';
import OutlinedDiv from '../../../../helpers/ui/OutlinedDiv';
import { getExtendedDeliveryRequestId } from '../../../../helpers/utils';
import { toYYYYMMDD } from '../../../../helpers/date-helper';
import { findCurrency } from '../../../../helpers/currency-helper';
import Currency from '../../../../models/currency';

interface EditStockReceivingProcessComponentProps {
  deliveryRequest: DeliveryRequestResponse;
  setDeliveryRequest: Dispatch<SetStateAction<DeliveryRequestResponse | null>>;
  supplierInfo: string;
  warehouseInfo: string;
  deliveryDate: string;
  createdDate: string;
  status: DeliveryRequestStatus;
  allowedSubsequentStatuses: DeliveryRequestStatus[];
}

const EditStockReceivingProcessComponent = ({
  deliveryRequest,
  setDeliveryRequest,
  supplierInfo,
  warehouseInfo,
  deliveryDate,
  createdDate,
  status,
  allowedSubsequentStatuses
}: EditStockReceivingProcessComponentProps) => {
  const classes = useStyles();
  const { stockReceivingProcessItems, loading, setLoading } = useStockReceivingProcessItems(
    deliveryRequest.id
  );
  const [newStatus, setNewStatus] = useState<Lookup>({
    id: status.key,
    value: status.label
  });
  const { stockReceivingProcess } = useStockReceivingProcess({ deliveryRequestId: deliveryRequest.id });
  const [currency, setCurrency] = useState<Currency | null>(null);

  useEffect(() => {
    if (!stockReceivingProcess) {
      return;
    }
    setCurrency(findCurrency(stockReceivingProcess.currency) ?? null)
  }, [stockReceivingProcess]);

  const patchStockReceivingProcess = useCallback(async () => {
    setLoading(true);
    let shouldShowSnackbar = false;
    if (newStatus.id !== status.key) {
      const updateDeliveryRequestRequest: UpdateDeliveryRequestRequest = {
        status: newStatus.id
      };
      const resp = await amsV3Service.updateDeliveryRequest(
        deliveryRequest.id,
        updateDeliveryRequestRequest
      );
      setDeliveryRequest(resp.data);
      shouldShowSnackbar = true;
    }
    if (newStatus.id === DeliveryRequestStatusKeys.FOR_INSPECTION) {
      const request: UpdateBatchesRequest = {
        items: stockReceivingProcessItems.map((item: StockReceivingProcessItemResponse) => ({
          itemId: item.itemId,
          quantity: item.documentQuantity,
          batchNumber: !!item.batchNumber
            ? item.batchNumber
            : `${deliveryRequest.id}_${toYYYYMMDD(new Date())}`,
          expirationDate:
            item.expirationDate ??
            toYYYYMMDD(new Date(new Date().getTime() + 20 * 365 * 24 * 60 * 60 * 1000)),
          countryOfOrigin: item.countryOfOrigin ?? 'BGR'
        })),
        requestType: 'update-batches'
      };
      await amsV3Service.updateStockReceivingProcessUpdateBatches(deliveryRequest.id, request);
      shouldShowSnackbar = true;
    }
    if (newStatus.id === DeliveryRequestStatusKeys.INSPECTED_WITH_REMARKS) {
      const request = {
        items: stockReceivingProcessItems.map((item: StockReceivingProcessItemResponse) => ({
          id: item.id,
          verifiedQuantity:
            item.verifiedQuantity && !isNaN(+item.verifiedQuantity) && +item.verifiedQuantity >= 0
              ? item.verifiedQuantity
              : item.documentQuantity
        }))
      };
      await amsV3Service.updateStockReceivingProcessVerifiedQuantities(deliveryRequest.id, request);
      setDeliveryRequest({ ...deliveryRequest, status: DeliveryRequestStatusKeys.VERIFIED });
      setNewStatus({
        id: DeliveryRequestStatusKeys.VERIFIED,
        value: DeliveryRequestStatusLabels.VERIFIED
      });
      shouldShowSnackbar = true;
    }
    snackbarService.setSnackbarOpen(shouldShowSnackbar);
    setLoading(false);
  }, [
    stockReceivingProcessItems,
    deliveryRequest,
    setDeliveryRequest,
    status,
    newStatus,
    setLoading
  ]);

  const [canUpdate] = usePermissions([AccessPermissions.CAN_UPDATE_DELIVERY]);

  const isFinalized = useMemo(
    () =>
      status === DeliveryRequestStatus.DELIVERED ||
      status === DeliveryRequestStatus.DECLINED_AFTER_VERIFICATION,
    [status]
  );

  return (
    <Paper elevation={2} className={classes.paper}>
      <Grid container spacing={2}>
        <Grid item lg={12}>
          <OutlinedDiv label="Данни от документ" disabled={true}>
            <Grid container spacing={2}>
              <Grid item sm={12} md={6} lg={2}>
                <AMSViewOnlyTextField
                  label="Номер"
                  value={getExtendedDeliveryRequestId(deliveryRequest.id)}
                />
              </Grid>
              <Grid item sm={12} md={6} lg={6}>
                <AMSViewOnlyTextField label="Склад" value={warehouseInfo} />
              </Grid>
              <Grid item sm={12} md={4} lg={2}>
                <AMSViewOnlyTextField label="Доставка на" value={deliveryDate} />
              </Grid>
              <Grid item sm={12} md={4} lg={2}>
                <AMSCurrencyAutocomplete
                  currency={currency}
                  disabled={true}
                  onChange={(_, currency: Currency | null) => {
                    setCurrency(currency);
                  }}
                />
              </Grid>
              <Grid item sm={12} md={6} lg={2}>
                <AMSViewOnlyTextField label="Създадена на" value={createdDate} />
              </Grid>
              <Grid item sm={12} md={6} lg={6}>
                <AMSViewOnlyTextField label="Доставчик" value={supplierInfo} />
              </Grid>
              <Grid item sm={12} md={6} lg={4}>
                <AMSAutocomplete
                  label="Статус"
                  options={
                    status
                      ? [status, ...allowedSubsequentStatuses].map((s) => ({
                          id: s.key,
                          value: s.label
                        }))
                      : []
                  }
                  value={newStatus}
                  onChange={(selectedValue: Lookup | null) => {
                    if (selectedValue) {
                      setNewStatus(selectedValue);
                    }
                  }}
                  minChar={0}
                  disabled={!canUpdate || isFinalized}
                  required
                />
              </Grid>
            </Grid>
          </OutlinedDiv>
        </Grid>
        <Grid item lg={12}>
          <Grid container spacing={2}>
            <Grid item lg={12}>
              <EditStockReceivingProcessTableComponent
                stockReceivingItems={stockReceivingProcessItems}
                status={getDeliveryRequestStatusByKey(newStatus.id as DeliveryRequestStatusKey)}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container item lg={12} justify="flex-end">
          {canUpdate && !isFinalized && (
            <AMSButton
              variant="contained"
              color="primary"
              className={classes.saveButton}
              onClick={patchStockReceivingProcess}
              text="Запиши"
              loading={loading}
            />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default EditStockReceivingProcessComponent;
